import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Info } from "lucide-react";
import { PRODUCTS } from "@/lib/site";
import SEO from "@/components/SEO";

export default function Products() {
  const { hash } = useLocation();

  // Smoothly scroll to hash on load
  React.useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [hash]);

  return (
    <div data-testid="products-page">
      <SEO
        title="Products"
        description="Recommended Fosroc products for reinforced concrete repair, Renderoc HB45, Nitoprime Zincrich Plus, Dekguard S, Nitomortar S and more. EN 1504 compliant repair system."
      />
      {/* Header */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-16 md:pt-32 md:pb-24">
          <div className="tech-label reveal">// Products · Specified Materials</div>
          <h1 className="hero-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl mt-6 max-w-5xl reveal reveal-delay-1">
            The materials<br />we specify.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-zinc-700 leading-relaxed reveal reveal-delay-2">
            Our default repair system is built around the{" "}
            <span className="font-semibold text-zinc-900">Fosroc Renderoc</span>{" "}
            range, the market-standard EN 1504 concrete-repair system in the
            UK. Below are the products we most commonly specify, why we choose
            them, and where each one fits in a repair.
          </p>
        </div>
      </section>

      {/* Disclaimer strip */}
      <section className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-start gap-3 text-xs md:text-sm text-zinc-600">
          <Info size={16} className="mt-0.5 text-[#C2410C] shrink-0" strokeWidth={2} />
          <p>
            <strong className="text-zinc-900">Note.</strong> Product data
            below is summarised from Fosroc technical datasheets for reference
            only. Always refer to the current manufacturer data sheet for
            application. All Fosroc, Renderoc, Nitoprime, Nitomortar, Patchroc
            and Dekguard trademarks belong to Fosroc International Ltd.
          </p>
        </div>
      </section>

      {/* Product cards */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="space-y-16">
          {PRODUCTS.map((p, i) => (
            <article
              key={p.slug}
              id={p.slug}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 border-t border-zinc-900 pt-16 first:border-t-0 first:pt-0 scroll-mt-32"
              data-testid={`product-${p.slug}`}
            >
              <div className="lg:col-span-4">
                <div className="lg:sticky lg:top-32">
                  <div className="tech-label">// {p.code} · {p.brand}</div>
                  <div className="mt-2 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                    {p.category}
                  </div>
                  <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight mt-4 leading-[0.95]">
                    {p.name}
                  </h2>
                  <div className="rule-line my-6 bg-zinc-900" />
                  <p className="text-zinc-700 text-base md:text-lg leading-relaxed">
                    {p.tagline}
                  </p>
                  {p.image && (
                    <div className="mt-6 border border-zinc-200 bg-zinc-50 p-3">
                      <div className="aspect-[300/186] overflow-hidden">
                        <img
                          src={p.image}
                          alt={`${p.name}, ${p.category}`}
                          loading="lazy"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-2 text-center">
                        Illustration · Fosroc International Ltd
                      </div>
                    </div>
                  )}
                  {p.fosrocUrl && (
                    <a
                      href={p.fosrocUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-900 hover:text-[#C2410C] transition-colors"
                      data-testid={`product-fosroc-link-${p.slug}`}
                    >
                      View on Fosroc.com <ArrowUpRight size={12} strokeWidth={2.5} />
                    </a>
                  )}
                </div>
              </div>
              <div className="lg:col-span-8">
                <p className="text-zinc-700 text-base md:text-lg leading-relaxed">
                  {p.summary}
                </p>

                <div className="mt-8">
                  <div className="tech-label text-[#C2410C]">// Specification</div>
                  <dl className="mt-4 border-t border-l border-zinc-200 grid grid-cols-1 md:grid-cols-2">
                    {p.spec.map((s, idx) => (
                      <div
                        key={idx}
                        className="border-r border-b border-zinc-200 p-4"
                      >
                        <dt className="text-[11px] font-mono uppercase tracking-[0.16em] text-zinc-500">
                          {s.k}
                        </dt>
                        <dd className="mt-1 text-zinc-900 text-sm">{s.v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <div className="mt-8">
                  <div className="tech-label text-[#C2410C]">// When we use it</div>
                  <ul className="mt-4 space-y-3">
                    {p.when.map((w, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 border-b border-zinc-200 pb-3"
                      >
                        <ChevronRight size={16} className="mt-1 text-[#C2410C] shrink-0" strokeWidth={2.5} />
                        <span className="text-zinc-800 text-sm md:text-base">{w}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative border border-zinc-900 p-10 md:p-16 bg-zinc-50">
          <span className="corner-tick tl" />
          <span className="corner-tick tr" />
          <span className="corner-tick bl" />
          <span className="corner-tick br" />
          <div className="tech-label">// Specification support</div>
          <h3 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight mt-4 leading-[0.95] max-w-3xl">
            Need a spec for your project?
          </h3>
          <p className="mt-6 max-w-2xl text-zinc-700">
            We&apos;ll write a project-specific repair specification citing
            appropriate Fosroc products, method statements and BS EN 1504
            references. Ideal for consulting engineers preparing tender
            documents.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn-primary" data-testid="products-cta-enquire">
              Request a specification <ArrowUpRight className="arrow" size={14} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
